import React, { useEffect, useState } from 'react';
import { FolderKanban } from 'lucide-react';
import { logGovernedMcp } from '../governedMcpLogger';
import { createId } from '../governedMcpStore';
import type { ToolGroup } from '../governedMcpTypes';
import { GatewayDrawer, fieldClass, ghostBtn, labelClass, primaryBtn } from '../components/GatewayUi';

function blankGroup(): ToolGroup {
  const now = new Date().toISOString();
  return {
    id: createId('grp'),
    name: '',
    description: '',
    createdAt: now,
    updatedAt: now,
  };
}

export default function ToolGroupDrawer({
  open,
  initial,
  isLight,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: ToolGroup | null;
  isLight: boolean;
  onClose: () => void;
  onSave: (group: ToolGroup) => void;
}) {
  const [form, setForm] = useState<ToolGroup>(blankGroup());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...initial } : blankGroup());
      setError(null);
      logGovernedMcp('app', 'tool_group_popup_open', { edit: Boolean(initial) });
    }
  }, [open, initial]);

  const submit = () => {
    const name = form.name.trim();
    if (!name) {
      setError('Group name is required.');
      logGovernedMcp('warning', 'tool_group_validation_failed', { reason: 'missing_name' });
      return;
    }
    onSave({ ...form, name, description: form.description.trim() });
  };

  return (
    <GatewayDrawer
      open={open}
      title={initial ? 'Edit tool group' : 'New tool group'}
      subtitle="Organize published tools so operators and agents can scan the catalog by domain."
      icon={<FolderKanban size={18} />}
      isLight={isLight}
      size="lg"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryBtn()} onClick={submit}>
            Save group
          </button>
        </div>
      }
    >
      <div className="space-y-4 max-w-xl">
        {error ? <p className="text-sm text-red-500">{error}</p> : null}
        <div>
          <label className={labelClass(isLight)}>Name</label>
          <input
            className={fieldClass(isLight)}
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Risk & Exposure"
          />
        </div>
        <div>
          <label className={labelClass(isLight)}>Description</label>
          <textarea
            className={fieldClass(isLight)}
            rows={3}
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="What this group contains and who should use it."
          />
        </div>
      </div>
    </GatewayDrawer>
  );
}
